import type { PermissionKey } from '@/lib/api'
import type { PlanId } from '@/lib/pricing'

export type Role = 'owner' | 'admin' | 'member' | 'front_desk' | 'finance' | 'platform_admin'

export type NavItemId =
  | 'dashboard'
  | 'proposals'
  | 'follow-ups'
  | 'booking-requests'
  | 'packages'
  | 'addons'
  | 'contract-templates'
  | 'earnings'
  | 'metrics'
  | 'preferences'
  | 'pricing'
  | 'billing'
  | 'recurring-plans'
  | 'integrations'
  | 'admin'
  | 'team'

export interface NavRequirement {
  id: NavItemId
  path: string
  labelKey: string
  icon?: string
  requiredPlan?: PlanId[]
  requiredPermission?: PermissionKey | PermissionKey[]
  roles?: Role[]
}

/**
 * Declarative registry of every app page's navigation/route requirements.
 * Consumed by AppShell (to build the sidebar) and AuthorizedRoute (to guard
 * deep links). Keep in sync with docs/specs/rbac-role-views.md §5.
 */
export const NAV_REGISTRY: NavRequirement[] = [
  { id: 'dashboard', path: '/dashboard', labelKey: 'common:navigation.dashboard', icon: 'House' },
  { id: 'proposals', path: '/proposals', labelKey: 'common:navigation.proposals', icon: 'FileText' },
  { id: 'follow-ups', path: '/follow-ups', labelKey: 'common:navigation.followUps', icon: 'ChatCircleText' },
  {
    id: 'booking-requests',
    path: '/booking-requests',
    labelKey: 'common:navigation.bookingRequests',
    icon: 'Envelope',
    requiredPlan: ['studio'],
  },
  { id: 'packages', path: '/packages', labelKey: 'common:navigation.packages', icon: 'Briefcase' },
  { id: 'addons', path: '/addons', labelKey: 'common:navigation.addons', icon: 'ShoppingCart' },
  {
    id: 'contract-templates',
    path: '/contract-templates',
    labelKey: 'common:navigation.contractTemplates',
    icon: 'Article',
  },
  {
    id: 'earnings',
    path: '/earnings',
    labelKey: 'common:navigation.earnings',
    icon: 'TrendUp',
    requiredPermission: 'canViewOwnReports',
  },
  {
    id: 'metrics',
    path: '/metrics',
    labelKey: 'common:navigation.metrics',
    icon: 'ChartBar',
    requiredPermission: 'canViewOwnReports',
  },
  {
    id: 'billing',
    path: '/billing',
    labelKey: 'common:navigation.billing',
    icon: 'CreditCard',
    requiredPermission: 'canManageSubscriptionBilling',
  },
  {
    id: 'team',
    path: '/team',
    // Owner/admin see the full team management page; member sees the
    // "Meu espaço" professional-profile fallback (TeamPage handles this
    // internally based on canManageTeam). front_desk/finance are excluded
    // entirely — they never had a team surface per §5.3/§5.6.
    labelKey: 'team:nav.team',
    icon: 'UsersThree',
    requiredPlan: ['studio'],
    roles: ['owner', 'admin', 'member'],
  },
  { id: 'integrations', path: '/integrations', labelKey: 'common:navigation.integrations' },
  {
    id: 'admin',
    path: '/admin',
    labelKey: 'common:navigation.admin',
    icon: 'ShieldCheck',
    roles: ['platform_admin'],
  },
  { id: 'preferences', path: '/preferences', labelKey: 'common:navigation.preferences', icon: 'Gear' },
]

export function getNavRequirement(id: NavItemId): NavRequirement | undefined {
  return NAV_REGISTRY.find((item) => item.id === id)
}

export function getNavRequirementByPath(path: string): NavRequirement | undefined {
  const normalized = path.replace(/\/+$/, '') || '/'
  return NAV_REGISTRY.find((item) => item.path === normalized)
}

interface AuthorizationInputs {
  plan: PlanId
  role: string | null
  can: (key: PermissionKey) => boolean
}

/**
 * Evaluates a NavRequirement against the caller's plan, role, and effective
 * permissions.
 *
 * Plan gating (`requiredPlan`) and role allowlists (`roles`) are always
 * enforced, including for owner/platform_admin — those are not permission
 * checks, they're entitlement/route-shape gates (e.g. the platform admin
 * console, or Studio-only surfaces). `requiredPermission` checks ARE
 * bypassed for owner/platform_admin, matching the backend guard's existing
 * fallback semantics (owner and platform_admin bypass all permission
 * checks — see spec §3.2).
 */
export function isNavItemAuthorized(item: NavRequirement, { plan, role, can }: AuthorizationInputs): boolean {
  if (item.requiredPlan && !item.requiredPlan.includes(plan)) {
    return false
  }

  // platform_admin passes role allowlists (it operates every tenant surface)
  // but still respects requiredPlan above — a non-Studio tenant has no team
  // surface even for platform staff.
  if (role === 'platform_admin') {
    return true
  }

  if (item.roles && (!role || !item.roles.includes(role as Role))) {
    return false
  }

  if (role === 'owner') {
    return true
  }

  if (item.requiredPermission) {
    const required = Array.isArray(item.requiredPermission) ? item.requiredPermission : [item.requiredPermission]
    if (!required.every((key) => can(key))) {
      return false
    }
  }

  return true
}

/**
 * Role's default landing page — used by AuthorizedRoute when redirecting
 * away from a page the caller isn't authorized to view. Every role lands on
 * the dashboard today; Dashboard itself already renders a role-appropriate
 * view (see Dashboard.tsx's canViewOrgReports check).
 */
export function getDefaultLandingPath(): string {
  return '/dashboard'
}
